import { SectionSaveTarget } from '@/components/app/entity-editor/section-save-target';
import { ServiceFootageFields } from '@/features/services-dashboard/service-footage-fields';
import { defaultServiceFootageChoices } from '@/features/services-dashboard/service-footage-fields';
import type { ServiceStockClips } from '@borradh-workspace/api-client/types';
import type { ServiceFootageChoices } from '@borradh-workspace/contracts';
import type { TreatmentCatalogueEntry } from '@borradh-workspace/labels';
import { useContext, useState } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { useSetServiceTags } from '../../api';
import type { Technique } from './stock-clip-tags-editor';

/** A treatment with the products or machines a service can name under it. */
export type TechniqueWithAgents = Technique & {
  agents: Array<{ slug: string; displayName: string }>;
};

interface ServiceTagsDialogProps {
  clinic?: boolean;
  organizationId: string;
  service: ServiceStockClips | null;
  techniques: TechniqueWithAgents[];
  catalogue?: TreatmentCatalogueEntry[];
  onOpenChange: (open: boolean) => void;
}

/**
 * Fix what a service shows on camera: the treatment, the product or machine
 * where a treatment has several, and the body areas the AI read from its name. They decide which stock clips match it automatically,
 * and once a person sets them the AI never re-reads the service.
 */
export function ServiceTagsDialog({
  clinic = false,
  organizationId,
  service,
  techniques,
  catalogue,
  onOpenChange,
}: ServiceTagsDialogProps) {
  return (
    <Dialog open={service !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        {service ? (
          <ServiceTagsEditor
            // A fresh editor per service, so nothing carries over.
            key={service.id}
            clinic={clinic}
            organizationId={organizationId}
            service={service}
            catalogue={catalogue}
            techniques={techniques}
            onClose={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export function ServiceTagsEditor({
  clinic = false,
  embedded = false,
  disabled = false,
  organizationId,
  service,
  techniques,
  catalogue = [],
  serviceName,
  onClose,
}: {
  clinic?: boolean;
  embedded?: boolean;
  disabled?: boolean;
  organizationId: string;
  service: ServiceStockClips;
  techniques: TechniqueWithAgents[];
  catalogue?: TreatmentCatalogueEntry[];
  serviceName?: string;
  onClose: () => void;
}) {
  const saveTarget = useContext(SectionSaveTarget);
  const saved = () => ({
    ...defaultServiceFootageChoices(),
    treatments: service.treatments ?? [],
    regions: service.treatments?.length ? [] : (service.regions ?? []),
    footageOtherFields: service.footageOtherFields ?? [],
  });
  const [choices, setChoices] = useState<ServiceFootageChoices>(saved);
  const { setServiceTags, isSavingTags } = useSetServiceTags(
    organizationId,
    clinic
  );
  const busy = disabled || isSavingTags;
  const setByPerson = service.specSource === 'declared';

  const changed =
    !setByPerson || JSON.stringify(choices) !== JSON.stringify(saved());

  const saveButton = (
    <Button
      type="button"
      className={embedded && !saveTarget ? 'w-full sm:w-auto' : undefined}
      disabled={busy || !changed}
      onClick={() => setServiceTags(service.id, choices, onClose)}
    >
      {isSavingTags ? 'Saving...' : 'Save treatment choices'}
    </Button>
  );

  return (
    <>
      {!embedded ? (
        <DialogHeader>
          <DialogTitle>What does {service.name} show?</DialogTitle>
          <DialogDescription>
            Choose the treatment and area for this service.
          </DialogDescription>
        </DialogHeader>
      ) : null}
      <ServiceFootageFields
        organizationId={organizationId}
        serviceName={serviceName ?? service.name}
        value={choices}
        onChange={setChoices}
        catalogue={catalogue}
        techniques={techniques}
        disabled={busy}
      />

      {embedded && saveTarget ? (
        createPortal(saveButton, saveTarget)
      ) : (
        <DialogFooter
          className={
            embedded
              ? 'gap-2 pt-3 sm:justify-between'
              : 'gap-2 sm:justify-between'
          }
        >
          <div className="flex w-full gap-2 sm:w-auto">
            {!embedded ? (
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </Button>
            ) : null}
            {saveButton}
          </div>
        </DialogFooter>
      )}
    </>
  );
}
